import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';

import { colors } from '@EVOCORP/corechat-theme';
import {
  Box,
  ButtonBase,
  CircularProgress,
  IconButton,
  Menu,
  Paper,
  Tooltip,
  Typography,
  Zoom,
} from '@material-ui/core';
import { blue, deepOrange, deepPurple, purple } from '@material-ui/core/colors';
import { makeStyles } from '@material-ui/core/styles';
import { CloseOutlined } from '@material-ui/icons';
import AddIcon from '@material-ui/icons/Add';
import ClearIcon from '@material-ui/icons/Clear';
import FormatQuoteIcon from '@material-ui/icons/FormatQuote';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import PersonIcon from '@material-ui/icons/Person';
import PhotoIcon from '@material-ui/icons/Photo';
import PhotoFilterIcon from '@material-ui/icons/PhotoFilter';
import SendIcon from '@material-ui/icons/Send';
import { useSnackbar } from 'notistack';
import { useModal } from 'react-modal-hook';
import { useDispatch } from 'react-redux';
import { useDebounce } from 'react-use';

import { ReactComponent as InteractiveIcon } from 'assets/images/add-interactive-message.svg';
import ActionButton from 'components/ActionButton';
import AudioRecorder from 'components/AudioRecorder';
import ButtonEncaminharMensagens from 'components/ButtonEncaminharMensagens';
import { ChatInput, ChatInputRef, MentionItem } from 'components/ChatInput';
import EmojiButton from 'components/EmojiButton';
import HiddenFileInput from 'components/HiddenFileInput';
import ModalEnviarMensagemCTA from 'components/ModalEnviarMensagemCTA';
import ModalGaleria from 'components/ModalGaleria';
import ModalListaContatos from 'components/ModalListaContatos';
import ModalRespostasProntas from 'components/ModalRespostasProntas';
import ReplyMessagePreview from 'components/ReplyMessagePreview';
import {
  LOCAL_STORAGE_KEY_INPUT,
  LOCAL_STORAGE_KEY_INPUT_MENTIONS,
} from 'constants/localStorageKeys';
import { useAppSelector } from 'store';
import { atendimentosSelector } from 'store/reducers/atendimentos';
import { TipoCanalEnum } from 'store/reducers/canais';
import { ContatoInterface } from 'store/reducers/contatos';
import {
  mensagensSelector,
  resetMensagensParaEncaminhar,
  resetReplyMessage,
} from 'store/reducers/mensagens';
import { whatsappToHtml } from 'utils/whatsappToHtml';

export declare interface InputFormMessageV2Props {
  maxChars?: number;
  isGroup?: boolean;
  onInputChange?: (value: string) => void;
  onSendInputText?: (
    value: string,
    callback: (isSuccess: boolean) => void,
  ) => void;
  onSendContato?: (contato: ContatoInterface) => void;
  onSendAudio?: (mediaBlob: Blob, duration: number) => void;
  onSendFromGaleria?: (storageIds: string[]) => void;
  onSendCTA?: () => void;
  onSelectFiles?: (files: File[]) => void;
}

const useStyles = makeStyles(({ palette, spacing }) => ({
  root: {
    padding: 0,
    marginTop: spacing(1),
    marginBottom: spacing(1),
  },
  rootInput: {
    display: 'flex',
    alignItems: 'center',
    padding: '4px',
    color: palette.text.secondary,
    '& > *': {
      margin: spacing(0, 1),
    },
  },
  inputContainer: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    padding: spacing(1, 2),
    borderRadius: spacing(3),
    backgroundColor: palette.background.default,
    cursor: 'text',
    '& > *:not(:first-child)': {
      marginLeft: spacing(1.5),
    },
  },
  paper: {
    marginTop: -spacing(3),
    background: 'transparent',
  },
  menuContent: {
    display: 'flex',
    flexDirection: 'column',
    padding: spacing(1),
    background: 'transparent',
    '& > *': {
      margin: spacing(1, 0),
    },
  },
  sendModeLabel: {
    fontSize: 12,
    color: palette.action.active,
  },
}));

export default function InputFormMessageV2(props: InputFormMessageV2Props) {
  const {
    maxChars = 6000,
    isGroup = false,
    onInputChange,
    onSendInputText,
    onSendContato,
    onSendAudio,
    onSendFromGaleria,
    onSendCTA,
    onSelectFiles,
  } = props;

  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [inputValue, setInputValue] = useState(() => {
    const shouldRestore =
      localStorage.getItem('restore_caption_flag') === 'true';
    if (shouldRestore) {
      return localStorage.getItem(LOCAL_STORAGE_KEY_INPUT) || '';
    }
    return '';
  });
  const [hasContent, setHasContent] = useState(() => {
    const shouldRestore =
      localStorage.getItem('restore_caption_flag') === 'true';
    if (shouldRestore) {
      return !!(localStorage.getItem(LOCAL_STORAGE_KEY_INPUT) || '').trim();
    }
    return false;
  });

  const chatInputRef = useRef<ChatInputRef>(null);

  // Persiste automaticamente o texto no localStorage (comportamento do input original)
  const DEBOUNCE_SAVE_DELAY_MS = 300;
  const [, cancelDebounce] = useDebounce(
    () => {
      if (inputValue.trim()) {
        localStorage.setItem(LOCAL_STORAGE_KEY_INPUT, inputValue);
      } else {
        localStorage.removeItem(LOCAL_STORAGE_KEY_INPUT);
      }
    },
    DEBOUNCE_SAVE_DELAY_MS,
    [inputValue],
  );

  const { atendimentoSelecionado } = useAppSelector(atendimentosSelector);
  const dispatch = useDispatch();
  const {
    isEncaminhandoMensagens,
    totalMensagensParaEncaminhar,
    replyMessage,
  } = useAppSelector(mensagensSelector);

  const tipoCanal = atendimentoSelecionado?.canal?.tipoCanal;

  const isOfficialWhatsAppChannel =
    tipoCanal === TipoCanalEnum.WHATSAPP_OFICIAL ||
    (atendimentoSelecionado?.canal?.isModoHibrido &&
      atendimentoSelecionado?.canal?.modoEnvio === 'API_OFICIAL');

  const isInstagramOrMessengerChannel =
    tipoCanal === TipoCanalEnum.INSTAGRAM ||
    tipoCanal === TipoCanalEnum.MESSENGER;

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  // Sem menções: retorna array vazio
  const fetchUsers = useCallback((): Promise<MentionItem[]> => {
    return Promise.resolve([]);
  }, []);

  const handleSendInputText = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;

      if (trimmed.length > maxChars) {
        enqueueSnackbar(
          `Você não pode enviar mais de ${maxChars} caracteres de uma vez.`,
        );
        return;
      }

      if (onSendInputText) {
        setLoading(true);
        cancelDebounce();
        localStorage.removeItem(LOCAL_STORAGE_KEY_INPUT);
        localStorage.removeItem(LOCAL_STORAGE_KEY_INPUT_MENTIONS);

        onSendInputText(trimmed, (isSuccess: boolean) => {
          setLoading(false);
          if (isSuccess) {
            chatInputRef.current?.clear();
            setInputValue('');
            setHasContent(false);
          }
          chatInputRef.current?.focus();
        });
      }
    },
    [enqueueSnackbar, maxChars, onSendInputText, cancelDebounce],
  );

  const handleSendContato = (contato: ContatoInterface) => {
    if (onSendContato) onSendContato(contato);
  };

  const handleSendAudio = (mediaBlob: Blob, duration: number) => {
    if (onSendAudio) onSendAudio(mediaBlob, duration);
  };

  const handleSendFilesFromGaleria = (storageIds: string[]) => {
    if (onSendFromGaleria) onSendFromGaleria(storageIds);
  };

  const prevAtendimentoIdRef = useRef<string | undefined>();

  useEffect(() => {
    if (
      prevAtendimentoIdRef.current !== undefined &&
      prevAtendimentoIdRef.current !== atendimentoSelecionado?.id
    ) {
      cancelDebounce();
      chatInputRef.current?.clear();
      setInputValue('');
      setHasContent(false);
      localStorage.removeItem(LOCAL_STORAGE_KEY_INPUT);
      localStorage.removeItem(LOCAL_STORAGE_KEY_INPUT_MENTIONS);
    }
    prevAtendimentoIdRef.current = atendimentoSelecionado?.id;
  }, [atendimentoSelecionado?.id, cancelDebounce]);

  useEffect(() => {
    localStorage.removeItem('restore_caption_flag');
  }, []);

  const handleSelectRespostaPronta = (texto: string) => {
    chatInputRef.current?.setContent(texto);
    chatInputRef.current?.focus();
  };

  const handleClearEditor = () => {
    cancelDebounce();
    chatInputRef.current?.clear();
    localStorage.removeItem(LOCAL_STORAGE_KEY_INPUT);
    localStorage.removeItem(LOCAL_STORAGE_KEY_INPUT_MENTIONS);
    setInputValue('');
    setHasContent(false);
  };

  const [showModalRespostasProntas, hideModalRespostasProntas] = useModal(
    ({ in: open }) => (
      <ModalRespostasProntas
        open={open}
        onClose={hideModalRespostasProntas}
        onSelect={handleSelectRespostaPronta}
      />
    ),
  );

  const [showModalListaContatos, hideModalListaContatos] = useModal(
    ({ in: open }) => (
      <ModalListaContatos
        open={open}
        onClose={hideModalListaContatos}
        onSelect={handleSendContato}
        filterByType={TipoCanalEnum.WHATSAPP}
      />
    ),
    [replyMessage?.id],
  );

  const [showModalGaleria, hideModalGaleria] = useModal(({ in: open }) => (
    <ModalGaleria
      open={open}
      onClose={hideModalGaleria}
      onSendFiles={handleSendFilesFromGaleria}
      isInstagramOrMessengerChannel={isInstagramOrMessengerChannel}
    />
  ));

  const [showModalEnviarMensagemCTA, hideModalEnviarMensagemCTA] = useModal(
    ({ in: open }) => (
      <ModalEnviarMensagemCTA
        open={open}
        onClose={hideModalEnviarMensagemCTA}
        onSendInteractiveMessage={onSendCTA}
      />
    ),
  );

  const handleSelectEmoji = (emoji: string) => {
    chatInputRef.current?.insertContent(emoji);
  };

  useEffect(() => {
    if (!replyMessage) return undefined;
    // Delay de 100ms para aguardar a animação do ReplyMessagePreview (comportamento original)
    const timeoutId = setTimeout(() => {
      chatInputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [replyMessage]);

  const handleClearReplyMessage = useCallback(() => {
    dispatch(resetReplyMessage());
  }, [dispatch]);

  const handleCancelEncaminharMensagens = useCallback(() => {
    dispatch(resetMensagensParaEncaminhar());
  }, [dispatch]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isEncaminhandoMensagens) {
        handleCancelEncaminharMensagens();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleCancelEncaminharMensagens, isEncaminhandoMensagens]);

  return (
    <Paper className={classes.root} elevation={0}>
      {atendimentoSelecionado?.canal?.isModoHibrido && (
        <Box style={{ paddingInline: 12, paddingBlock: 8 }}>
          <Typography className={classes.sendModeLabel}>
            Via:{' '}
            <span style={{ fontWeight: 500 }}>
              {atendimentoSelecionado?.canal?.modoEnvio === 'API_OFICIAL'
                ? 'WhatsApp Oficial'
                : 'WhatsApp Coex QR Code'}
            </span>
          </Typography>
        </Box>
      )}

      {replyMessage && (
        <ReplyMessagePreview
          replyMessage={replyMessage}
          isGroup={isGroup}
          atendimentoSelecionado={atendimentoSelecionado}
          handleClearReplyMessage={handleClearReplyMessage}
        />
      )}
      <Paper className={classes.rootInput} elevation={0}>
        {isEncaminhandoMensagens ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexGrow={1}
          >
            <Box
              style={{
                height: 48,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <IconButton onClick={handleCancelEncaminharMensagens}>
                <CloseOutlined />
              </IconButton>{' '}
              {totalMensagensParaEncaminhar > 0 ? (
                <>
                  {totalMensagensParaEncaminhar}
                  {totalMensagensParaEncaminhar === 1
                    ? ' mensagem selecionada'
                    : ' mensagens selecionadas'}
                </>
              ) : (
                'Nenhuma mensagem selecionada'
              )}
            </Box>

            <ButtonEncaminharMensagens />
          </Box>
        ) : (
          <>
            <EmojiButton onSelect={handleSelectEmoji} />

            <Box>
              <ButtonBase
                disableRipple
                disabled={loading}
                onClick={handleClick}
              >
                <AddIcon />
              </ButtonBase>
              <Menu
                elevation={0}
                id="simple-menu"
                anchorEl={anchorEl}
                classes={{ paper: classes.paper }}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              >
                <Box className={classes.menuContent}>
                  {isOfficialWhatsAppChannel &&
                    atendimentoSelecionado?.sessaoStatus &&
                    onSendCTA && (
                      <ActionButton
                        handleClose={handleClose}
                        bgColor={colors.light.primary.main}
                        tooltip="Mensagem CTA URL"
                        onClick={showModalEnviarMensagemCTA}
                      >
                        <InteractiveIcon />
                      </ActionButton>
                    )}

                  {onSendContato && !isInstagramOrMessengerChannel && (
                    <ActionButton
                      handleClose={handleClose}
                      tooltip="Contato"
                      bgColor={blue[500]}
                      onClick={showModalListaContatos}
                    >
                      <PersonIcon />
                    </ActionButton>
                  )}

                  {onSendFromGaleria && (
                    <ActionButton
                      handleClose={handleClose}
                      bgColor={purple[500]}
                      tooltip="Arquivos da galeria"
                      onClick={showModalGaleria}
                    >
                      <PhotoFilterIcon />
                    </ActionButton>
                  )}

                  {onSelectFiles && (
                    <ActionButton
                      htmlFor="input-send-image"
                      handleClose={handleClose}
                      bgColor={deepOrange[500]}
                      tooltip="Imagens"
                    >
                      <PhotoIcon />
                      <HiddenFileInput
                        id="input-send-image"
                        accept="image/jpeg, image/png"
                        multiple
                        onFiles={onSelectFiles}
                      />
                    </ActionButton>
                  )}

                  {onSelectFiles && !isInstagramOrMessengerChannel && (
                    <ActionButton
                      htmlFor="input-send-document"
                      handleClose={handleClose}
                      bgColor={deepPurple[500]}
                      tooltip="Documento"
                    >
                      <InsertDriveFileIcon />
                      <HiddenFileInput
                        id="input-send-document"
                        accept="*"
                        multiple
                        onFiles={onSelectFiles}
                      />
                    </ActionButton>
                  )}
                </Box>
              </Menu>
            </Box>

            <Box
              className={classes.inputContainer}
              onClick={() => chatInputRef.current?.focus()}
            >
              <Tooltip title="Respostas prontas" placement="top">
                <ButtonBase
                  disabled={loading}
                  onClick={showModalRespostasProntas}
                  disableRipple
                >
                  <FormatQuoteIcon color="inherit" />
                </ButtonBase>
              </Tooltip>

              <ChatInput
                id="input-send-message"
                ref={chatInputRef}
                fetchUsers={fetchUsers}
                disableMentions
                onSend={handleSendInputText}
                onInputChange={(text, hasCnt) => {
                  setHasContent(hasCnt);
                  setInputValue(text);
                  localStorage.removeItem(LOCAL_STORAGE_KEY_INPUT_MENTIONS);
                  if (onInputChange) onInputChange(text);
                }}
                showModalRespostasProntas={showModalRespostasProntas}
                disabled={loading}
                placeholder="Digite # para respostas prontas"
                autoFocus
                initialValue={(() => {
                  const shouldRestore =
                    localStorage.getItem('restore_caption_flag') === 'true';
                  if (shouldRestore) {
                    return whatsappToHtml(
                      localStorage.getItem(LOCAL_STORAGE_KEY_INPUT) || '',
                    );
                  }
                  return '';
                })()}
              />

              <Zoom in={hasContent}>
                <ButtonBase
                  disabled={loading}
                  onClick={handleClearEditor}
                  disableRipple
                >
                  <ClearIcon />
                </ButtonBase>
              </Zoom>
            </Box>

            {hasContent ? (
              <ButtonBase
                onClick={() => {
                  chatInputRef.current?.send();
                }}
                disableRipple
              >
                {loading ? (
                  <CircularProgress style={{ width: 24, height: 24 }} />
                ) : (
                  <SendIcon color="primary" />
                )}
              </ButtonBase>
            ) : (
              <AudioRecorder onEnded={handleSendAudio} />
            )}
          </>
        )}
      </Paper>
    </Paper>
  );
}
